import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';
import getUsaStates from '../../translations/usaStates';

import FieldPhoneNumberWithCountryInput from '../FieldPhoneNumberWithCountryInput/FieldPhoneNumberWithCountryInput';
import FieldSelect from '../FieldSelect/FieldSelect';
import FieldTextInput from '../FieldTextInput/FieldTextInput';

import css from './ShippingAddressForm.module.css';

const US_ARMED_FORCES_CITIES = [
  { code: 'APO', name: 'APO' },
  { code: 'FPO', name: 'FPO' },
  { code: 'DPO', name: 'DPO' },
];

const US_ARMED_FORCES_STATES = ['AA', 'AE', 'AP'];
const DEFAULT_COUNTRY_CODE = 'US';

export const DEFAULT_SHIPPING_ADDRESS_FIELD_NAMES = {
  name: 'name',
  phone: 'phone',
  street: 'street1',
  apt: 'streetNo',
  country: 'country',
  city: 'city',
  state: 'state',
  zip: 'zip',
};

export const CHECKOUT_SHIPPING_ADDRESS_FIELD_NAMES = {
  name: 'recipientName',
  phone: 'recipientPhoneNumber',
  street: 'recipientAddressLine1',
  apt: 'recipientAddressLine2',
  country: 'recipientCountry',
  city: 'recipientCity',
  state: 'recipientState',
  zip: 'recipientPostal',
};

/**
 * Shared shipping address fields used on checkout, account settings, and listing edit.
 *
 * @param {Object} props
 * @param {string} props.formId
 * @param {Object} props.form Final Form API
 * @param {Object} props.values Current form values
 * @param {Object} [props.names] Field name map
 * @param {boolean} [props.disabled]
 * @param {string} [props.locale]
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const ShippingAddressFields = props => {
  const {
    formId,
    form,
    values = {},
    names = DEFAULT_SHIPPING_ADDRESS_FIELD_NAMES,
    disabled = false,
    locale,
    className,
  } = props;
  const intl = useIntl();
  const requireText = intl.formatMessage({ id: 'ShippingAddressForm.requireText' });
  const countryName =
    getCountryCodes(locale).find(countryOption => countryOption.code === DEFAULT_COUNTRY_CODE)
      ?.name || 'United States';
  const states = getUsaStates();
  const state = values?.[names.state];
  const isMilitaryState = US_ARMED_FORCES_STATES.includes(state);
  const prevIsMilitaryStateRef = useRef(isMilitaryState);

  const cityValue = values?.[names.city];
  useEffect(() => {
    form.change(names.country, DEFAULT_COUNTRY_CODE);
  }, [form, names.country]);

  useEffect(() => {
    if (prevIsMilitaryStateRef.current !== isMilitaryState && cityValue) {
      form.change(names.city, undefined);
    }
    prevIsMilitaryStateRef.current = isMilitaryState;
  }, [isMilitaryState, form, names.city, cityValue]);

  const nameRequired = validators.required(requireText);
  const phoneRequired = validators.required(requireText);
  const phoneValid = validators.validPhoneNumber(
    intl.formatMessage({ id: 'ShippingAddressForm.invalidPhoneNumber' })
  );
  const streetRequired = validators.required(requireText);
  const cityRequired = validators.required(requireText);
  const zipRequired = validators.required(requireText);
  const stateRequired = validators.required(requireText);

  return (
    <div className={className}>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${formId}.${names.name}`}
          name={names.name}
          disabled={disabled}
          className={classNames(css.fullField, css.noMarginTop)}
          type="text"
          autoComplete="shipping name"
          label={intl.formatMessage({ id: 'ShippingAddressForm.nameLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.namePlaceholder' })}
          validate={nameRequired}
          onUnmount={() => form.change(names.name, undefined)}
        />
      </div>
      <div className={css.formRow}>
        <FieldPhoneNumberWithCountryInput
          id={`${formId}.${names.phone}`}
          name={names.phone}
          disabled={disabled}
          className={classNames(css.field, css.fullField)}
          autoComplete="shipping tel"
          label={intl.formatMessage({ id: 'ShippingAddressForm.phoneLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.phonePlaceholder' })}
          validate={validators.composeValidators(phoneRequired, phoneValid)}
          onUnmount={() => form.change(names.phone, undefined)}
        />
      </div>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${formId}.${names.street}`}
          name={names.street}
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="shipping address-line1"
          label={intl.formatMessage({ id: 'ShippingAddressForm.streetLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.streetPlaceholder' })}
          validate={streetRequired}
          onUnmount={() => form.change(names.street, undefined)}
        />
        <FieldTextInput
          id={`${formId}.${names.apt}`}
          name={names.apt}
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="shipping address-line2"
          label={intl.formatMessage({ id: 'ShippingAddressForm.aptLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.aptPlaceholder' })}
          onUnmount={() => form.change(names.apt, undefined)}
        />
      </div>
      <div className={css.formRow}>
        <FieldTextInput
          id={`${formId}.${names.country}`}
          name={names.country}
          className={classNames(css.field, css.lockedField)}
          type="text"
          label={intl.formatMessage({ id: 'ShippingAddressForm.countryLabel' })}
          disabled
          readOnly
          format={() => countryName}
          parse={() => DEFAULT_COUNTRY_CODE}
        />
        {isMilitaryState ? (
          <FieldSelect
            id={`${formId}.${names.city}`}
            name={names.city}
            disabled={disabled}
            className={css.field}
            label={intl.formatMessage({ id: 'ShippingAddressForm.cityLabel' })}
            validate={cityRequired}
          >
            <option disabled value="">
              {intl.formatMessage({ id: 'ShippingAddressForm.cityPlaceholder' })}
            </option>
            {US_ARMED_FORCES_CITIES.map(city => (
              <option key={city.code} value={city.code}>
                {city.name}
              </option>
            ))}
          </FieldSelect>
        ) : (
          <FieldTextInput
            id={`${formId}.${names.city}`}
            name={names.city}
            disabled={disabled}
            className={css.field}
            type="text"
            autoComplete="shipping address-level2"
            label={intl.formatMessage({ id: 'ShippingAddressForm.cityLabel' })}
            placeholder={intl.formatMessage({ id: 'ShippingAddressForm.cityPlaceholder' })}
            validate={cityRequired}
            onUnmount={() => form.change(names.city, undefined)}
          />
        )}
      </div>
      <div className={css.formRow}>
        <FieldSelect
          id={`${formId}.${names.state}`}
          name={names.state}
          disabled={disabled}
          className={css.field}
          label={intl.formatMessage({ id: 'ShippingAddressForm.stateLabel' })}
          validate={stateRequired}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'ShippingAddressForm.statePlaceholder' })}
          </option>
          {states.map(stateOption => (
            <option key={stateOption.code} value={stateOption.code}>
              {stateOption.name}
            </option>
          ))}
        </FieldSelect>
        <FieldTextInput
          id={`${formId}.${names.zip}`}
          name={names.zip}
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="shipping postal-code"
          label={intl.formatMessage({ id: 'ShippingAddressForm.zipLabel' })}
          placeholder={intl.formatMessage({ id: 'ShippingAddressForm.zipPlaceholder' })}
          validate={zipRequired}
          onUnmount={() => form.change(names.zip, undefined)}
        />
      </div>
    </div>
  );
};

export default ShippingAddressFields;
